// Background starfield
// Parallax scrolling for depth effect

import { VectorRenderer } from "../graphics/VectorRenderer";
import { randomRange, randomInt } from "../utils/math";

interface Star {
  x: number;
  y: number;
  layer: number; // 0 = far, 1 = mid, 2 = near
  color: string; // Pre-computed color
  baseSize: number; // Pre-computed base size
  parallaxFactor: number; // Pre-computed parallax
  twinkleOffset: number; // Random offset for twinkle
}

export class Stars {
  private stars: Star[];
  private screenWidth: number;
  private screenHeight: number;
  private wrapWidth: number;
  private wrapHeight: number;
  // Reusable Vec2 objects to avoid allocations
  private startPoint = { x: 0, y: 0 };
  private endPoint = { x: 0, y: 0 };

  constructor(screenWidth: number, screenHeight: number, count: number = 100) {
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;
    this.wrapWidth = screenWidth * 2;
    this.wrapHeight = screenHeight * 2;
    this.stars = [];

    // Generate random stars with pre-computed values
    for (let i = 0; i < count; i++) {
      const brightness = randomRange(0.3, 1);
      const alpha = Math.floor(brightness * 255).toString(16).padStart(2, "0");
      const layer = randomInt(0, 2);

      this.stars.push({
        x: randomRange(0, this.wrapWidth),
        y: randomRange(0, this.wrapHeight),
        layer,
        color: `#${alpha}${alpha}${alpha}`,
        baseSize: 1 + layer * 0.5,
        parallaxFactor: 0.2 + layer * 0.3, // Faster parallax: 0.2, 0.5, 0.8 per layer
        twinkleOffset: randomRange(0, Math.PI * 2),
      });
    }
  }

  render(renderer: VectorRenderer, cameraX: number, cameraY: number = 0): void {
    // Cache time calculation once per frame
    const time = Date.now() / 500;

    // Batch all star draws for better performance
    renderer.beginBatch();

    for (let i = 0; i < this.stars.length; i++) {
      const star = this.stars[i];

      // Calculate screen position with parallax for both axes
      let screenX = star.x - cameraX * star.parallaxFactor;
      let screenY = star.y - cameraY * star.parallaxFactor;

      // Wrap around
      screenX = ((screenX % this.wrapWidth) + this.wrapWidth) % this.wrapWidth;
      screenY = ((screenY % this.wrapHeight) + this.wrapHeight) % this.wrapHeight;

      // Skip if off screen
      if (screenX < 0 || screenX > this.screenWidth) continue;
      if (screenY < 0 || screenY > this.screenHeight) continue;

      // Twinkle effect using pre-computed offset
      const twinkle = Math.sin(time + star.twinkleOffset) * 0.3 + 0.7;
      const size = star.baseSize * twinkle;

      // Reuse point objects instead of creating new ones
      this.startPoint.x = screenX;
      this.startPoint.y = screenY;
      this.endPoint.x = screenX + size;
      this.endPoint.y = screenY;

      renderer.drawLine(this.startPoint, this.endPoint, star.color, size);
    }

    renderer.endBatch();
  }
}
